import { Link, useNavigate } from "react-router-dom";
import { goBackOr } from "@/lib/goBackOr";
import Navbar from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { ArrowLeft, Building2, FileText, Scale } from "lucide-react";

export const CGU_VERSION = "1.0";

const LAST_UPDATED = "24 avril 2026";

const TOC = [
  { id: "s1", label: "Objet et acceptation" },
  { id: "s2", label: "Description du service" },
  { id: "s3", label: "Conditions d'inscription" },
  { id: "s4", label: "Obligations du vendeur" },
  { id: "s5", label: "Obligations de l'acheteur" },
  { id: "s6", label: "Système de paiement et séquestre" },
  { id: "s7", label: "Enregistrement des appels" },
  { id: "s8", label: "Notation et signalement" },
  { id: "s9", label: "Limitation de responsabilité" },
  { id: "s10", label: "Protection des données (RGPD)" },
  { id: "s11", label: "Propriété intellectuelle" },
  { id: "s12", label: "Résiliation et bannissement" },
  { id: "s13", label: "Droit applicable et juridiction" },
  { id: "s14", label: "Modification des CGU" },
];

const CGU = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Conditions générales d'utilisation (CGU)"
        description="CGU de la marketplace RdvMarket (rdvmarket.fr) — KLIKANGO SAS."
        canonicalPath="/cgu"
      />
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-x-6 mb-6">
          <button
            type="button"
            className="inline-flex w-fit items-center gap-1 text-sm text-slate-500 transition-colors hover:text-[#2D7FF9]"
            onClick={() => goBackOr(navigate)}
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="inline-flex items-center gap-2 bg-[#2D7FF9]/10 text-[#2D7FF9] px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase shrink-0 self-end sm:self-auto">
            <Scale className="w-3.5 h-3.5" />
            CGU · Version {CGU_VERSION}
          </div>
        </div>

        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#0B0D12] mb-2 pb-4 border-b-2 border-[#2D7FF9]/25">
          Conditions générales d&apos;utilisation
        </h1>
        <p className="text-slate-500 mb-6">
          Dernière mise à jour : {LAST_UPDATED}. Plateforme <strong className="text-[#2D7FF9]">RdvMarket</strong>{" "}
          (<span className="whitespace-nowrap">rdvmarket.fr</span>) — marketplace exploitée par{" "}
          <strong>KLIKANGO SAS</strong>.
        </p>

        <div className="border border-[#E5E7EB] rounded-lg p-5 mb-8 bg-slate-50 flex items-start gap-3 text-sm text-slate-700">
          <Building2 className="w-5 h-5 text-[#2D7FF9] shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p>
              <strong className="text-[#0B0D12]">Éditeur :</strong> KLIKANGO SAS — société par actions simplifiée,
              exploitant la marque et la plateforme <strong>RdvMarket</strong> accessible à l&apos;adresse{" "}
              <a href="https://rdvmarket.fr" className="text-[#2D7FF9] font-semibold hover:underline">
                rdvmarket.fr
              </a>
              .
            </p>
            <p>
              <strong className="text-[#0B0D12]">Activité :</strong> marketplace de mise en relation entre, d&apos;une
              part, des <strong>vendeurs</strong> de dossiers clients qualifiés (prospects/clients, professionnels ou
              particuliers selon l&apos;annonce) et, d&apos;autre part, des <strong>acheteurs professionnels</strong>
              souhaitant obtenir des rendez-vous ou contacts commerciaux qualifiés.
            </p>
            <p className="text-xs text-slate-500">
              Siège social : 16 Avenue Sainte-Marie, 94160 Saint-Mandé, France — RCS Créteil — SIREN 948 465 331 — TVA
              FR42 948 465 331.
            </p>
          </div>
        </div>

        <nav
          className="mb-10 rounded-lg border border-[#E5E7EB] bg-white p-5 shadow-sm"
          aria-label="Sommaire des conditions générales d'utilisation"
        >
          <div className="flex items-center gap-2 mb-4 text-[#2D7FF9]">
            <FileText className="w-5 h-5" />
            <span className="font-display font-bold text-lg tracking-tight text-[#0B0D12]">Sommaire</span>
          </div>
          <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700 marker:text-[#2D7FF9] marker:font-semibold">
            {TOC.map((item, i) => (
              <li key={item.id} className="pl-1">
                <a href={`#${item.id}`} className="text-[#2D7FF9] hover:underline font-medium">
                  {i + 1}. {item.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="prose prose-slate max-w-none space-y-12 text-sm text-slate-700 leading-relaxed" data-testid="cgu-content">
          <section id="s1" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              1. Objet et acceptation
            </h2>
            <p>
              Les présentes <strong>Conditions générales d&apos;utilisation</strong> (ci-après les «{" "}
              <strong>CGU</strong> ») ont pour objet de définir les règles d&apos;accès et d&apos;utilisation de la
              plateforme <strong>RdvMarket</strong> (ci-après la « <strong>Plateforme</strong> »), ainsi que les droits
              et obligations des <strong>utilisateurs</strong> (vendeurs et acheteurs professionnels).
            </p>
            <p className="mt-2">
              <strong>
                L&apos;utilisation de la Plateforme vaut acceptation pleine et entière des présentes CGU, sans réserve.
              </strong>{" "}
              Si l&apos;utilisateur n&apos;accepte pas tout ou partie des CGU, il doit cesser toute utilisation de la
              Plateforme. L&apos;éditeur se réserve le droit de refuser l&apos;accès aux services à tout utilisateur
              ne respectant pas les CGU.
            </p>
          </section>

          <section id="s2" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              2. Description du service
            </h2>
            <ul className="list-disc ml-5 space-y-2">
              <li>
                RdvMarket est un service de <strong>mise en relation</strong> entre vendeurs et acheteurs
                <strong> professionnels</strong> de dossiers clients qualifiés, accessible en ligne sur{" "}
                <strong>rdvmarket.fr</strong>. Les prospects/clients concernés par ces dossiers peuvent être des{" "}
                <strong>professionnels ou des particuliers</strong> selon les annonces publiées.
              </li>
              <li>
                La Plateforme agit en qualité d&apos;<strong>intermédiaire technique</strong> (hébergement, mise en
                relation, outils de communication, paiement via prestataires tiers tels que Stripe). Elle ne réalise pas
                elle-même de démarchage commercial pour le compte des utilisateurs.
              </li>
              <li>
                Les appels effectués via la Plateforme ont pour objet de <strong>recontacter</strong> des prospects/clients
                (par exemple dans le cadre d&apos;une <strong>demande de rappel</strong>, d&apos;une relation commerciale
                existante, ou de toute autre base légale applicable) afin de <strong>fixer un rendez-vous</strong> ou de
                finaliser une vente. Il est <strong>interdit</strong> d&apos;utiliser la Plateforme pour réaliser des appels
                de prospection <strong>non sollicités</strong> en violation du droit applicable.
              </li>
              <li>
                En cas de réclamation, contrôle ou procédure, la Plateforme peut <strong>coopérer avec les autorités</strong>
                et transmettre les informations strictement nécessaires (journaux techniques, identifiants, enregistrements
                d&apos;appel) dans le respect du cadre légal.
              </li>
              <li>
                <strong>Aucune garantie de résultat commercial</strong> (nombre de rendez-vous obtenus, taux de
                conversion, chiffre d&apos;affaires, etc.) n&apos;est donnée par l&apos;éditeur. La conclusion ou non
                d&apos;affaires entre utilisateurs et tiers relève de leur seule responsabilité.
              </li>
              <li>
                Les fonctionnalités (marketplace, négociation, appels, séquestre, notation, etc.) peuvent évoluer ; la
                description figurant sur le site ou dans les CGU est indicative et peut être adaptée pour des raisons
                légales, techniques ou commerciales, dans le respect de la section 14 ci-dessous.
              </li>
            </ul>
          </section>

          <section id="s3" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              3. Conditions d&apos;inscription
            </h2>
            <ul className="list-disc ml-5 space-y-2">
              <li>
                L&apos;inscription et l&apos;utilisation des services sont <strong>strictement réservées aux
                professionnels</strong> (personnes morales ou physiques agissant dans le cadre d&apos;une activité
                économique, artisanale, libérale ou agricole, hors consommateur au sens du code de la consommation).
              </li>
              <li>
                L&apos;utilisateur <strong>certifie sur l&apos;honneur exercer une activité professionnelle</strong> et
                agir en tant que professionnel. Il garantit l&apos;exactitude des informations fournies à
                l&apos;inscription (identité, coordonnées, société, pays, etc.).
              </li>
              <li>
                <strong>Toute fausse déclaration engage la responsabilité personnelle et exclusive de
                l&apos;utilisateur</strong> à l&apos;égard de l&apos;éditeur, des autres utilisateurs et des tiers
                (notamment prospects et autorités).
              </li>
              <li>
                La Plateforme se réserve le droit de <strong>demander à tout moment des justificatifs</strong> (extrait
                Kbis, immatriculation, pièce d&apos;identité, attestation d&apos;activité, etc.) et de suspendre
                l&apos;accès au compte le temps du contrôle.
              </li>
              <li>
                En cas de <strong>fausse déclaration avérée ou de fraude</strong>, le compte pourra être{" "}
                <strong>immédiatement suspendu ou clôturé sans indemnité ni remboursement</strong> des sommes déjà
                engagées (abonnements, commissions, soldes techniques), sous réserve des droits impératifs applicables et
                des stipulations relatives au séquestre lorsque des fonds sont en cours de traitement.
              </li>
            </ul>
          </section>

          <section id="s4" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              4. Obligations du vendeur
            </h2>
            <p>En publiant un dossier sur RdvMarket, le vendeur certifie notamment que :</p>
            <ul className="list-disc ml-5 space-y-2 mt-2">
              <li>
                le <strong>prospect a été contacté</strong> et a été <strong>informé qu&apos;un commercial va le
                rappeler</strong> (ou formulation équivalente conforme aux usages du secteur et au droit applicable) ;
              </li>
              <li>
                les <strong>informations du dossier sont exactes et complètes</strong> au mieux de sa connaissance ;
              </li>
              <li>
                il <strong>n&apos;a pas publié ce même dossier simultanément sur une autre plateforme</strong> ou canal
                de vente concurrent, sauf mention expresse et conforme aux règles affichées sur la Plateforme le cas
                échéant.
              </li>
            </ul>
            <p className="mt-3">
              En cas de <strong>fausse déclaration</strong> établie selon les éléments objectifs dont dispose
              l&apos;éditeur (dont enregistrements d&apos;appels, journaux techniques, signalements contradictoires), le
              vendeur <strong>renonce à tout recours</strong> contre l&apos;éditeur et l&apos;acheteur concernant le
              litige ainsi qualifié et <strong>accepte le remboursement automatique de l&apos;acheteur sans arbitrage
              humain préalable</strong>, sans préjudice des actions judiciaires possibles entre les parties ou à
              l&apos;égard du vendeur.
            </p>
            <p className="mt-2">
              Si <strong>trois (3) acheteurs successifs</strong> refusent le rendez-vous ou la qualification pour le
              même dossier selon les critères affichés sur la Plateforme, l&apos;<strong>annonce pourra être
              automatiquement retirée</strong> ou désactivée.
            </p>
            <p className="mt-2">
              En cas de <strong>fraudes répétées</strong> ou de gravité particulière, l&apos;éditeur pourra prononcer un{" "}
              <strong>bannissement définitif</strong>, sans préjudice des poursuites civiles ou pénales.
            </p>
          </section>

          <section id="s5" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              5. Obligations de l&apos;acheteur
            </h2>
            <ul className="list-disc ml-5 space-y-2">
              <li>
                L&apos;acheteur <strong>s&apos;engage à statuer</strong> (valider ou refuser le rendez-vous selon les options
                prévues) <strong>dans les douze (12) heures</strong> suivant la mise en relation téléphonique (fin de
                l&apos;appel ou dernier échange enregistré selon les critères techniques de la Plateforme).
              </li>
              <li>
                <strong>Au-delà de ce délai de douze (12) heures</strong>, si l&apos;acheteur n&apos;a pas statué, la Plateforme{" "}
                <strong>analyse l&apos;ensemble des enregistrements</strong> d&apos;appel de la transaction afin de détecter un{" "}
                <strong>contournement</strong> (récupération ou échange de coordonnées hors canal). En l&apos;absence de statut
                explicite, le rendez-vous est alors <strong>réputé validé</strong> et le paiement peut être finalisé ;{" "}
                <strong>l&apos;acheteur conserve la possibilité d&apos;ouvrir un litige</strong> dans le délai affiché après
                clôture (généralement aligné sur le délai de versement vendeur), <strong>sauf</strong> si une violation de
                contournement a été constatée par l&apos;analyse. Un refus explicite avant ce délai suit les règles d&apos;arbitrage
                habituelles (enregistrements, motif écrit, etc.).
              </li>
              <li>
                À titre de <strong>bonne pratique commerciale</strong> (sans créer d&apos;obligation juridique distincte), il est{" "}
                <strong>vivement conseillé</strong> de viser une <strong>confirmation concrète du créneau</strong> du rendez-vous{" "}
                <strong>dans les soixante-douze (72) heures</strong> suivant l&apos;appel lorsque les circonstances le permettent.
              </li>
              <li>
                Toute <strong>tentative de contacter le prospect en dehors de la Plateforme</strong> (contournement des
                mécanismes d&apos;anonymisation ou de traçabilité) emporte{" "}
                <strong>perte définitive du droit au remboursement</strong> sur la transaction concernée et peut
                entraîner un <strong>bannissement</strong>.
              </li>
              <li>
                Tout <strong>refus abusif ou de mauvaise foi</strong> (notamment sans motif légitime alors que les
                critères annoncés sont remplis) peut entraîner la <strong>perte du remboursement</strong> et un{" "}
                <strong>bannissement</strong> après examen des éléments (y compris enregistrements et historiques).
              </li>
              <li>
                Si le <strong>client est injoignable après cinq (5) tentatives</strong> de prise de contact via les
                fonctionnalités prévues par la Plateforme, l&apos;acheteur peut <strong>refuser le dossier</strong> ou{" "}
                <strong>ouvrir un litige</strong> avec les éléments attendus. L&apos;annonce peut en outre être{" "}
                <strong>retirée automatiquement du catalogue</strong> (statut archivé) lorsque la Plateforme enregistre cinq (5)
                appels terminés sans échange exploitable selon ses critères techniques (durée d&apos;appel ; paramètres
                communiqués dans la documentation ou l&apos;interface). Dans ce cas de figure automatique, les acheteurs dont la
                transaction est encore au stade <strong>séquestre / avant déblocage définitif</strong> font l&apos;objet d&apos;un{" "}
                <strong>remboursement automatique</strong> (annulation ou reversement selon l&apos;état du paiement Stripe),
                sous réserve du bon fonctionnement du prestataire de paiement. Dès qu&apos;un appel dépasse le seuil fixé et traduit un
                contact réel avec le prospect, cette automatisation ne s&apos;applique pas sur ce fondement.
              </li>
              <li>
                L&apos;<strong>abonnement acheteur</strong>, lorsqu&apos;il est proposé sur la Plateforme, est réputé{" "}
                <strong>non remboursable une fois activé</strong> pour la période en cours, sous réserve des droits
                impératifs du droit français.
              </li>
            </ul>
          </section>

          <section id="s6" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              6. Système de paiement et séquestre
            </h2>
            <ul className="list-disc ml-5 space-y-2">
              <li>
                Les fonds versés par l&apos;acheteur sont <strong>bloqués sous séquestre</strong> via le prestataire de
                paiement <strong>Stripe</strong> (ou équivalent) jusqu&apos;à <strong>confirmation du rendez-vous</strong>{" "}
                ou jusqu&apos;à l&apos;application des règles automatiques décrites aux présentes CGU et sur l&apos;interface.
              </li>
              <li>
                Une <strong>commission vendeur de 10 %</strong> (montant indicatif, TVA le cas échéant selon les mentions
                affichées au moment du paiement) est <strong>prélevée automatiquement</strong> sur le prix de vente ou
                selon les modalités Stripe Connect en vigueur.
              </li>
              <li>
                Lorsqu&apos;un <strong>abonnement acheteur</strong> est proposé, les tarifs indicatifs communiqués sur
                la Plateforme sont actuellement de l&apos;ordre de <strong>249 € par mois</strong> (facturation mensuelle)
                ou de <strong>199 € par mois</strong> sous forme d&apos;<strong>engagement annuel</strong> (facturation
                annuelle correspondante), sous réserve de modification affichée sur le site ou à la souscription.
              </li>
              <li>
                <strong>Aucun remboursement de l&apos;abonnement</strong> n&apos;est dû après activation pour la
                période payée, sauf disposition légale impérative contraire.
              </li>
            </ul>
          </section>

          <section id="s7" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              7. Enregistrement des appels
            </h2>
            <ul className="list-disc ml-5 space-y-2">
              <li>
                Les <strong>appels passés via la Plateforme peuvent être enregistrés automatiquement</strong>, dans les
                limites techniques et légales applicables.
              </li>
              <li>
                L&apos;utilisateur <strong>consent explicitement</strong> à cet enregistrement{" "}
                <strong>en utilisant la fonctionnalité d&apos;appel</strong> de la Plateforme, sans préjudice des
                mentions affichées au moment de l&apos;appel (message vocal, bandeau, etc.).
              </li>
              <li>
                Les enregistrements sont <strong>conservés cinq (5) ans</strong>, sauf obligation légale de durée
                différente ou anonymisation anticipée lorsque la finalité disparaît.
              </li>
              <li>
                Ils sont utilisés <strong>uniquement</strong> en cas de <strong>litige</strong>, de{" "}
                <strong>lutte contre la fraude</strong> ou pour des traitements d&apos;<strong>analyse automatisée
                interne</strong> à des fins anti-fraude, dans le respect du RGPD.
              </li>
              <li>
                Un <strong>message vocal</strong> ou équivalent <strong>avertit les parties</strong> au début de
                l&apos;appel lorsque la configuration technique le permet.
              </li>
            </ul>
          </section>

          <section id="s8" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              8. Système de notation et signalement
            </h2>
            <ul className="list-disc ml-5 space-y-2">
              <li>
                Après chaque transaction conclue sur la Plateforme, un <strong>système de notation de 1 à 5
                étoiles</strong> (et, le cas échéant, un commentaire modéré) peut être proposé aux parties.
              </li>
              <li>
                Les <strong>signalements</strong> doivent être fondés et sincères. À titre de règle contractuelle,{" "}
                <strong>trois (3) signalements validés</strong> (après examen par l&apos;éditeur ou selon des règles
                automatisées de gravité) peuvent entraîner un <strong>bannissement automatique ou une suspension
                prolongée</strong> du compte concerné.
              </li>
              <li>
                Les <strong>signalements abusifs ou calomnieux</strong> peuvent être sanctionnés (suspension,
                bannissement, transmission aux autorités en cas d&apos;infraction pénale).
              </li>
            </ul>
          </section>

          <section id="s9" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              9. Limitation de responsabilité
            </h2>
            <ul className="list-disc ml-5 space-y-2">
              <li>
                RdvMarket / KLIKANGO SAS agit comme <strong>intermédiaire technique</strong> et n&apos;est pas partie aux
                contrats de vente de dossiers conclus entre utilisateurs, sauf stipulation expresse contraire pour
                certaines prestations accessoires.
              </li>
              <li>
                L&apos;éditeur <strong>ne garantit pas la qualité commerciale</strong> des dossiers, ni leur conformité
                à des attentes subjectives de l&apos;acheteur.
              </li>
              <li>
                L&apos;éditeur <strong>ne garantit pas la conclusion effective de rendez-vous</strong> ou de contrats
                entre l&apos;acheteur et les prospects.
              </li>
              <li>
                Sauf faute lourde ou dol prouvé, la <strong>responsabilité de l&apos;éditeur</strong> à l&apos;égard
                d&apos;une transaction litigieuse est <strong>limitée au montant des commissions effectivement
                perçues</strong> par l&apos;éditeur sur cette transaction (hors mandat d&apos;intermédiation financière
                spécifique imposé par la loi).
              </li>
              <li>
                L&apos;éditeur n&apos;est <strong>pas responsable du contenu des informations</strong> fournies par les
                utilisateurs (fiches dossier, messages, pièces jointes) ; ceux-ci restent seuls responsables.
              </li>
            </ul>
          </section>

          <section id="s10" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              10. Protection des données (RGPD)
            </h2>
            <ul className="list-disc ml-5 space-y-2">
              <li>
                <strong>Données collectées</strong> (liste non exhaustive) : identité, civilité,{" "}
                <strong>nom, e-mail, téléphone</strong>, société / organisation, pays, identifiants de compte, données de
                facturation et de paiement traitées par Stripe, <strong>historique des transactions</strong>, contenus
                publiés, échanges sur la Plateforme, données de connexion et de sécurité (dont la{" "}
                <strong>dernière adresse IP</strong> observée lors des connexions authentifiées, mise à jour de façon
                espacée à des fins anti-fraude), enregistrements d&apos;appels lorsque la fonctionnalité est utilisée.
              </li>
              <li>
                <strong>Durée de conservation</strong> : les données nécessaires à l&apos;exécution du contrat et aux
                obligations légales peuvent être conservées jusqu&apos;à <strong>cinq (5) ans après la fin de la
                relation contractuelle</strong> ou la clôture du compte, sauf durée différente imposée par la loi (ex.
                comptabilité, LCB-FT).
              </li>
              <li>
                Pour l&apos;exercice des droits d&apos;<strong>accès, rectification, effacement, limitation,
                portabilité, opposition</strong> (dans les limites légales) :{" "}
                <a href="mailto:contact@rdvmarket.fr" className="text-[#2D7FF9] font-semibold hover:underline">
                  contact@rdvmarket.fr
                </a>
                . Une réclamation peut être portée auprès de la CNIL (www.cnil.fr).
              </li>
              <li>
                Les <strong>enregistrements d&apos;appels</strong> : durée de conservation indiquée à la section 7 ;
                accès principalement en cas de <strong>litige</strong> ou traitement anti-fraude, par personnel
                habilité.
              </li>
              <li>
                <strong>Pas de vente des données personnelles à des tiers</strong> à des fins commerciales externes ;
                des sous-traitants techniques (hébergement, paiement, anti-fraude) peuvent traiter des données strictement
                sur instruction et dans le respect du RGPD.
              </li>
            </ul>
            <p className="mt-2 text-xs text-slate-500">
              Pour les traitements liés aux données des prospects cédées dans les dossiers, se référer également à la
              charte d&apos;utilisation et aux engagements RGPD affichés sur la Plateforme.
            </p>
          </section>

          <section id="s11" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              11. Propriété intellectuelle
            </h2>
            <ul className="list-disc ml-5 space-y-2">
              <li>
                La <strong>Plateforme</strong> (logiciels, charte graphique, textes hors contenus utilisateurs, marques{" "}
                <strong>RdvMarket</strong>, bases de données structurelles, documentation) est protégée par le droit de
                la propriété intellectuelle et <strong>appartient à KLIKANGO SAS</strong> ou à ses concédants de licence.
              </li>
              <li>
                L&apos;utilisateur bénéficie d&apos;une <strong>licence d&apos;usage personnelle, non exclusive,
                non transférable et révocable</strong> pour l&apos;accès aux services, dans le cadre de son activité
                professionnelle.
              </li>
              <li>
                Les <strong>utilisateurs conservent leurs droits</strong> sur les contenus qu&apos;ils publient
                (descriptions de dossiers, messages, etc.), tout en concédant à l&apos;éditeur une licence d&apos;hébergement,
                de reproduction et de représentation strictement nécessaire à l&apos;exploitation de la Plateforme.
              </li>
            </ul>
          </section>

          <section id="s12" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              12. Résiliation et bannissement
            </h2>
            <ul className="list-disc ml-5 space-y-2">
              <li>
                L&apos;utilisateur peut <strong>résilier son compte à tout moment</strong> selon les modalités prévues
                sur l&apos;interface (sous réserve de la liquidation des opérations en cours et des obligations légales
                de conservation).
              </li>
              <li>
                Un <strong>bannissement automatique</strong> peut intervenir après{" "}
                <strong>trois (3) signalements</strong> validés conformément à la section 8.
              </li>
              <li>
                En cas de <strong>fraude avérée</strong>, l&apos;éditeur pourra prononcer un <strong>bannissement
                immédiat</strong>, <strong>sans préavis</strong>, et <strong>sans remboursement</strong> des sommes déjà
                acquises au titre de l&apos;abonnement ou des services, sous réserve des droits impératifs.
              </li>
              <li>
                Les <strong>fonds en séquestre (escrow)</strong> au moment d&apos;une suspension ou d&apos;un
                bannissement pourront être <strong>gelés dans l&apos;attente d&apos;un arbitrage interne</strong>, d&apos;une
                décision judiciaire ou de l&apos;application des règles Stripe / bancaires applicables.
              </li>
            </ul>
          </section>

          <section id="s13" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              13. Droit applicable et juridiction
            </h2>
            <ul className="list-disc ml-5 space-y-2">
              <li>
                Les présentes CGU sont régies par le <strong>droit français</strong>.
              </li>
              <li>
                <strong>Tribunal compétent :</strong> les tribunaux du ressort de la <strong>Cour d&apos;appel de
                Paris</strong>, sous réserve de règles d&apos;ordre public de compétence non dérogatoires.
              </li>
              <li>
                En cas de <strong>litige</strong>, les parties s&apos;engagent à rechercher une{" "}
                <strong>solution amiable</strong> (échange d&apos;emails avec le support, médiation éventuelle)
                <strong> avant toute saisine judiciaire</strong>, pendant un délai raisonnable communiqué par
                l&apos;éditeur (sauf urgence manifestement justifiée ou mesures conservatoires).
              </li>
            </ul>
          </section>

          <section id="s14" className="scroll-mt-24">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3 border-b border-[#2D7FF9]/20 pb-2">
              14. Modification des CGU
            </h2>
            <p>
              KLIKANGO SAS se réserve le droit de <strong>modifier les présentes CGU</strong> à tout moment, notamment
              pour refléter l&apos;évolution des services, de la réglementation ou des pratiques de sécurité.
            </p>
            <p className="mt-2">
              Les utilisateurs en seront informés par tout moyen utile (notification sur la Plateforme, e-mail à
              l&apos;adresse du compte, bandeau de connexion). Sauf disposition impérative contraire, la poursuite de
              l&apos;utilisation des services après un <strong>préavis de trente (30) jours</strong> suivant la
              publication ou la notification vaut <strong>acceptation des CGU mises à jour</strong>. En cas de refus,
              l&apos;utilisateur doit mettre fin à son utilisation et résilier son compte selon les modalités prévues.
            </p>
          </section>
        </div>

        <div className="mt-14 border-t border-[#E5E7EB] pt-6 flex flex-wrap items-center gap-3 text-xs text-slate-400">
          <FileText className="w-4 h-4 shrink-0" />
          <span>
            Document contractuel — pour la charte RGPD détaillée, voir aussi{" "}
            <Link to="/charte" className="text-[#2D7FF9] hover:underline">
              /charte
            </Link>
            .
          </span>
          <span className="ml-auto">Version {CGU_VERSION}</span>
        </div>
      </div>
    </div>
  );
};

export default CGU;
